import React, { useRef, useState, useEffect } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { X, DownloadSimple, Printer, Copy, Check } from '@phosphor-icons/react';
import { toast } from 'sonner';
import api from '../../utils/api';

/**
 * Show QR Code modal — generates a scannable QR encoding the public
 * show URL. Producers can download it as a PNG (for social media posts),
 * print a full-page flyer, or copy the raw link.
 *
 * The QR is rendered via `<QRCodeCanvas>` so we can grab the pixel data
 * off the underlying <canvas> for a real PNG download (no server round-
 * trip needed). Rendered at 256px, exported at 512px so print stays
 * crisp on paper.
 */
export const ShowQRCodeModal = ({ show, url, onClose, onGenerated }) => {
  const canvasWrapRef = useRef(null);
  const [copied, setCopied] = useState(false);
  // v239 — Single-QR-per-show. When the modal opens we hit
  // POST /qr/mark-generated (idempotent on the backend) to (a) audit-log
  // who generated the QR first, and (b) hydrate `qr_generated_at` so we
  // can display "First generated Feb 25, 2026 by Nate" underneath the code.
  // The actual QR pattern is always identical regardless of when/how many
  // times someone opens the modal — it encodes the deterministic public
  // URL — so "one QR per show" is naturally enforced.
  const [genInfo, setGenInfo] = useState({
    at: show?.qr_generated_at || null,
    by: show?.qr_generated_by || null,
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post(`/shows/${show.id}/qr/mark-generated`);
        if (cancelled) return;
        setGenInfo({ at: data.qr_generated_at, by: data.qr_generated_by });
        if (!data.already_generated) onGenerated?.(data);
      } catch {
        // Non-fatal — QR still displays, we just skip the audit stamp.
      }
    })();
    return () => { cancelled = true; };
  }, [show?.id, onGenerated]);

  const getCanvas = () => {
    if (!canvasWrapRef.current) return null;
    return canvasWrapRef.current.querySelector('canvas');
  };

  const filename = () => {
    const safe = (show?.name || 'show').replace(/[^a-z0-9]+/gi, '_').slice(0, 40) || 'show';
    return `${safe}_qr.png`;
  };

  const handleDownload = () => {
    const canvas = getCanvas();
    if (!canvas) { toast.error('QR not ready yet — try again'); return; }
    const link = document.createElement('a');
    link.href = canvas.toDataURL('image/png');
    link.download = filename();
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('QR code downloaded');
  };

  const handlePrint = () => {
    const canvas = getCanvas();
    if (!canvas) { toast.error('QR not ready yet — try again'); return; }
    const dataUrl = canvas.toDataURL('image/png');
    const escape = (s) => String(s || '').replace(/[&<>"']/g, (c) => (
      { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
    ));
    const html = `<!doctype html><html><head><title>${escape(show?.name || 'Show QR')}</title><meta charset="utf-8"/>
      <style>
        *{-webkit-print-color-adjust:exact !important;print-color-adjust:exact !important}
        body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;color:#0A0A0A;margin:0;padding:0;text-align:center;background:#fff}
        .page{padding:0.6in 0.4in;min-height:calc(100vh - 1.2in);display:flex;flex-direction:column;align-items:center;justify-content:center;gap:24px}
        h1{margin:0;font-size:36px;font-weight:900;letter-spacing:-0.5px}
        .tag{color:#0096FF;font-size:16px;font-weight:800;letter-spacing:2px;text-transform:uppercase;margin-top:4px}
        .qr{padding:20px;background:#fff;border:3px solid #0A0A0A;border-radius:16px;box-shadow:0 8px 32px rgba(0,0,0,0.1)}
        .qr img{display:block;width:340px;height:340px;image-rendering:pixelated}
        .cta{font-size:22px;font-weight:800;color:#0A0A0A}
        .url{font-size:14px;color:#666;font-family:monospace;word-break:break-all;max-width:400px}
        .footer{color:#999;font-size:11px;margin-top:auto}
        @media print{@page{margin:0.4in}}
      </style></head><body>
      <div class="page">
        <div>
          <h1>${escape(show?.name || 'Horse Show')}</h1>
          <div class="tag">SCAN TO VIEW &amp; ENTER</div>
        </div>
        <div class="qr"><img src="${dataUrl}" alt="QR code"/></div>
        <div class="cta">Point your phone camera at the QR code</div>
        <div class="url">${escape(url)}</div>
        <div class="footer">Powered by Show Master</div>
      </div>
      <script>window.onload=()=>{window.print()};</script>
      </body></html>`;
    const w = window.open('', '_blank');
    if (w) { w.document.write(html); w.document.close(); }
  };

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success('Link copied');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt('Copy this link:', url);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="show-qr-modal"
    >
      <div
        className="bg-[#161616] border border-[#262626] rounded-xl w-full max-w-md flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between px-5 py-4 border-b border-[#262626]">
          <div>
            <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit' }}>Share via QR Code</h3>
            <p className="text-xs text-[#9E9E9E] mt-0.5">
              Anyone who scans lands directly on this show&rsquo;s page &mdash; no login required.
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-[#9E9E9E] hover:text-white p-1"
            data-testid="show-qr-close-btn"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-6 flex flex-col items-center gap-4">
          <div
            ref={canvasWrapRef}
            className="bg-white p-4 rounded-lg border-2 border-[#FFCC00]/40"
            data-testid="show-qr-canvas-wrap"
          >
            <QRCodeCanvas
              value={url}
              size={256}
              level="H"
              includeMargin={false}
              bgColor="#FFFFFF"
              fgColor="#0A0A0A"
            />
          </div>
          <div className="text-center max-w-full">
            <p className="text-white font-bold text-sm truncate max-w-[300px] mx-auto" style={{ fontFamily: 'Outfit' }}>
              {show?.name || 'Horse Show'}
            </p>
            <p className="text-[10px] text-[#555] font-mono mt-1 break-all">{url}</p>
            {genInfo.at && (
              <p className="text-[10px] text-[#666] mt-2" data-testid="show-qr-generated-info">
                First generated {new Date(genInfo.at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                {genInfo.by ? <> by <span className="text-[#9E9E9E]">{genInfo.by}</span></> : null}
              </p>
            )}
          </div>
        </div>

        <div className="px-5 py-4 border-t border-[#262626] grid grid-cols-3 gap-2">
          <button
            onClick={handleDownload}
            data-testid="show-qr-download-btn"
            className="flex flex-col items-center gap-1 px-2 py-2.5 rounded-md text-xs font-semibold bg-[#FFCC00]/10 border border-[#FFCC00]/40 text-[#FFCC00] hover:bg-[#FFCC00]/20 transition-colors"
          >
            <DownloadSimple size={16} weight="bold" />
            Download PNG
          </button>
          <button
            onClick={handlePrint}
            data-testid="show-qr-print-btn"
            className="flex flex-col items-center gap-1 px-2 py-2.5 rounded-md text-xs font-semibold bg-[#0096FF]/10 border border-[#0096FF]/40 text-[#0096FF] hover:bg-[#0096FF]/20 transition-colors"
          >
            <Printer size={16} weight="bold" />
            Print Flyer
          </button>
          <button
            onClick={handleCopyUrl}
            data-testid="show-qr-copy-link-btn"
            className="flex flex-col items-center gap-1 px-2 py-2.5 rounded-md text-xs font-semibold bg-[#161616] border border-[#262626] text-[#9E9E9E] hover:text-white hover:border-[#0096FF]/40 transition-colors"
          >
            {copied ? <Check size={16} weight="bold" /> : <Copy size={16} weight="bold" />}
            {copied ? 'Copied' : 'Copy Link'}
          </button>
        </div>
      </div>
    </div>
  );
};
